import type { SettingsSnapshot } from "@magic-md/engine-client/config";
import type { ClientConfigOverlay } from "@magic-md/engine-client/protocol";

export interface LoadClientExecutionConfigOptions {
    liveSnapshot: SettingsSnapshot | null;
    loadSnapshot: () => Promise<SettingsSnapshot>;
    assertCurrent?: () => void;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
    if (!value || typeof value !== "object" || Array.isArray(value)) return false;
    const proto = Object.getPrototypeOf(value);
    return proto === Object.prototype || proto === null;
}

export function extractUserPluginConfig(
    snapshot: SettingsSnapshot,
): NonNullable<ClientConfigOverlay["plugins"]> {
    const plugins: NonNullable<ClientConfigOverlay["plugins"]> = {};

    for (const [key, meta] of Object.entries(snapshot.meta.entries ?? {})) {
        if (!key.startsWith("plugins.") || !meta.setInTargets?.user) {
            continue;
        }

        const match = key.match(
            /^plugins\.([^.]+)\.(enabled|settings|defaults)$/,
        );
        if (!match) continue;

        const [, pluginName, field] = match;
        const userValue = meta.targetValues?.user;
        if (userValue === undefined) continue;

        const pluginConfig = plugins[pluginName] ?? {};
        if (field === "enabled" && typeof userValue === "boolean") {
            pluginConfig.enabled = userValue;
        }
        if (
            (field === "settings" || field === "defaults") &&
            isPlainObject(userValue)
        ) {
            pluginConfig[field] = userValue;
        }
        plugins[pluginName] = pluginConfig;
    }

    return plugins;
}

export function toClientExecutionConfig(
    snapshot: SettingsSnapshot,
): ClientConfigOverlay | undefined {
    const plugins = extractUserPluginConfig(snapshot);
    return Object.keys(plugins).length > 0 ? { plugins } : undefined;
}

/**
 * Converts the settings projection used by plugin execution and coalesces the
 * fallback snapshot request. reset() advances a generation so a request from a
 * previous Engine identity can never populate the new cache.
 */
export class ClientExecutionConfigCache {
    private cachedSnapshot: SettingsSnapshot | null = null;
    private cachedConfig: ClientConfigOverlay | undefined;
    private pending: Promise<ClientConfigOverlay | undefined> | null = null;
    private generation = 0;

    load(
        options: LoadClientExecutionConfigOptions,
    ): Promise<ClientConfigOverlay | undefined> {
        const { liveSnapshot, loadSnapshot, assertCurrent } = options;
        if (liveSnapshot) {
            if (this.cachedSnapshot === liveSnapshot) {
                return Promise.resolve(this.cachedConfig);
            }
            const nextConfig = toClientExecutionConfig(liveSnapshot);
            this.cachedSnapshot = liveSnapshot;
            this.cachedConfig = nextConfig;
            return Promise.resolve(nextConfig);
        }

        if (this.pending) return this.pending;

        const generation = this.generation;
        let pending!: Promise<ClientConfigOverlay | undefined>;
        pending = loadSnapshot()
            .then((snapshot) => {
                assertCurrent?.();
                const nextConfig = toClientExecutionConfig(snapshot);
                if (generation === this.generation) {
                    this.cachedSnapshot = snapshot;
                    this.cachedConfig = nextConfig;
                }
                return nextConfig;
            })
            .finally(() => {
                if (this.pending === pending) {
                    this.pending = null;
                }
            });
        this.pending = pending;
        return pending;
    }

    reset(): void {
        this.generation += 1;
        this.cachedSnapshot = null;
        this.cachedConfig = undefined;
        this.pending = null;
    }
}
